import React from 'react';
import {
  Box,
  Chip,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import HistoryToggleOffIcon from '@mui/icons-material/HistoryToggleOff';
import { signedMoney } from './drawerFormat.js';

const HistoryEntry = ({ entry, index, isLast, onSelectEntry }) => (
  <Stack direction="row" spacing={1.5} sx={{ alignItems: 'stretch' }}>
    <Box sx={{ width: 14, position: 'relative', flexShrink: 0 }}>
      <Box
        sx={{
          position: 'absolute',
          top: 6,
          left: 3,
          width: 8,
          height: 8,
          borderRadius: '50%',
          bgcolor: index === 0 ? 'primary.main' : 'primary.light',
          outline: '2px solid',
          outlineColor: 'background.paper',
          boxShadow: index === 0 ? '0 0 0 3px rgba(25, 118, 210, 0.15)' : 'none',
        }}
      />
      {!isLast && (
        <Box
          sx={{
            position: 'absolute',
            top: 16,
            left: 6,
            bottom: -4,
            width: 2,
            bgcolor: 'divider',
          }}
        />
      )}
    </Box>
    <Box
      role="button"
      tabIndex={0}
      onClick={() => onSelectEntry?.(entry)}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onSelectEntry?.(entry);
        }
      }}
      sx={{
        flex: 1,
        minWidth: 0,
        pb: isLast ? 0 : 1.5,
        mb: isLast ? 0 : 0.5,
        px: 1,
        py: 0.75,
        ml: -1,
        borderRadius: 1,
        cursor: 'pointer',
        transition: 'background-color 80ms',
        '&:hover': {
          bgcolor: 'action.hover',
          '& .history-chevron': { transform: 'translateX(2px)', opacity: 1 },
        },
        '&:focus-visible': {
          outline: '2px solid',
          outlineColor: 'primary.main',
          outlineOffset: 2,
        },
      }}
    >
      <Stack direction="row" alignItems="baseline" spacing={1} sx={{ mb: 0.5, flexWrap: 'wrap' }}>
        <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.primary' }}>
          {entry.date}
        </Typography>
        <Chip
          size="small"
          label={entry.serviceMonth}
          variant="outlined"
          sx={{ height: 18, '& .MuiChip-label': { px: 0.75, fontSize: 10 } }}
        />
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          by {entry.reviewedBy}
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Typography variant="caption" sx={{ fontWeight: 700, color: 'error.main' }}>
          {signedMoney(entry.maxDelta)}
        </Typography>
        <ChevronRightIcon
          className="history-chevron"
          fontSize="small"
          sx={{ color: 'text.secondary', opacity: 0.4, transition: 'transform 120ms, opacity 120ms' }}
        />
      </Stack>
      <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5, mb: 0.5 }}>
        <Chip size="small" label={entry.category} variant="outlined" />
        <Chip size="small" label={entry.remediation} variant="outlined" color="primary" />
        {entry.relatedTickets?.length > 0 && (
          <Chip size="small" label={`${entry.relatedTickets.length} ticket${entry.relatedTickets.length > 1 ? 's' : ''}`} variant="outlined" sx={{ fontSize: 10 }} />
        )}
        {entry.noteThread?.length > 0 && (
          <Chip size="small" label={`${entry.noteThread.length} notes`} variant="outlined" sx={{ fontSize: 10 }} />
        )}
      </Stack>
      {entry.notes && (
        <Typography
          variant="caption"
          sx={{
            color: 'text.secondary',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {entry.notes}
        </Typography>
      )}
    </Box>
  </Stack>
);

const VarianceHistoryTimeline = ({ history = [], onSelectEntry }) => (
  <Paper variant="outlined" sx={{ p: 2, borderRadius: 1.5 }}>
    <Stack spacing={1.5}>
      <Stack direction="row" alignItems="center" spacing={1}>
        <HistoryToggleOffIcon fontSize="small" color="action" />
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          Prior variances for this BAC
        </Typography>
        <Chip size="small" label={history.length} variant="outlined" sx={{ height: 20, '& .MuiChip-label': { px: 0.75, fontSize: 11 } }} />
      </Stack>
      {history.length === 0 ? (
        <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
          No prior variances recorded for this dealer. If this is resolved, it will become the first entry.
        </Typography>
      ) : (
        <Stack spacing={0}>
          {history.map((entry, index) => (
            <HistoryEntry
              key={`${entry.date}-${index}`}
              entry={entry}
              index={index}
              isLast={index === history.length - 1}
              onSelectEntry={onSelectEntry}
            />
          ))}
        </Stack>
      )}
    </Stack>
  </Paper>
);

export default VarianceHistoryTimeline;
